import {
  ConnectionPositionPair,
  Overlay,
  OverlayConfig
} from '@angular/cdk/overlay';
import { Injectable } from '@angular/core';
import { ComponentPortal } from '@angular/cdk/portal';
import { BaseOverlay } from './abtract-overlay';
import { PositionOverlay, WithOffset } from 'src/app/shared';
import { debounceTime } from 'rxjs/operators';

@Injectable()
export class XOverlayService<T, K> extends BaseOverlay<T, K> {
  constructor(protected overlay: Overlay) {
    super(overlay);
  }

  /**
   * Init component
   * @returns ComponentPortal
   */
  component(): ComponentPortal<T> {
    return null;
  }

  /**
   * Init Position
   * @returns ConnectionPositionPair[]
   */
  position(): ConnectionPositionPair[] {
    return [
      WithOffset(PositionOverlay.bottomCenter, 0, 10),
      WithOffset(PositionOverlay.topCenter, 0, -10)
    ];
  }

  /**
   * Init default config
   * @param element HTMLElement
   * @param position ConnectionPositionPair[]
   * @returns OverlayConfig
   */
  config(
    element: HTMLElement,
    position?: ConnectionPositionPair[]
  ): OverlayConfig {
    return {
      hasBackdrop: false,
      disposeOnNavigation: true,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(element)
        .withPositions(position || this.position())
        .withFlexibleDimensions(true)
        .withViewportMargin(40)
        .withGrowAfterOpen(true)
        .withPush(false)
    };
  }

  /**
   * Open overlay
   * @param element 
   * @param data 
   * @param position 
   * @returns void
   */
  open(
    element: HTMLElement,
    data: K,
    position?: ConnectionPositionPair[]
  ): void {
    if (this.preventShow || this.isOpen) {
      return;
    }

    this.isOpen = true;
    this._element = element;

    this._overlayRef?.dispose(); // clean profile overlay when open new overlay
    this._overlayRef = this.overlay.create(this.config(element, position)); // create overlay with config
    
    this._componentRef = this._overlayRef.attach(this.component()); // add component to overlay

    this._componentRef.setInput('data', data);
    this._componentRef.setInput('overlay', this);

    this._overlayRef
      .outsidePointerEvents()
      .pipe(debounceTime(200))
      .subscribe(() => {
        this._overlayRef.dispose();
      });
  }

  /**
   * Hide overlay
   */
  hide() {
    if (!this.preventHide && this._overlayRef) {
      this._overlayRef.dispose();
    }

    this.isOpen = false;
  }

  /**
   * Re-update position
   */
  updatePosition(position) {
    this._overlayRef?.updatePositionStrategy(
      this.overlay
        .position()
        .flexibleConnectedTo(this._element)
        .withPositions(position)
        .withFlexibleDimensions(true)
        .withViewportMargin(40)
        .withGrowAfterOpen(true)
        .withPush(false)
    );
    this._overlayRef?.updatePosition();
  }

  /**
   * Re-update position and data
   */
  updatePositionWithData(data) {
    this._overlayRef?.updatePosition();
    this._componentRef?.setInput('data', data);
  }
}
